import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import Markdown from "../Markdown.jsx";
import Select from "../Select.jsx";
import LaunchCard from "../LaunchCard.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { labelForMemory } from "../../lib/memoryNames.js";
import { dashboardScopeToApiScope } from "../../lib/analyticsClient.js";
import { useLabels, scopeStepLabel } from "../../lib/dimensionDefs.js";
import { getChatCreds } from "../../lib/chatCreds.js";
import { getRankedModels, defaultModelFor } from "../../lib/modelCatalog.js";
import { clusterMemories, clusterCoverage, mergeCluster } from "../../lib/memoryXray.js";
import { memoryEndpointMatrix } from "../../lib/memories.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../../lib/authTokenStore.js";

// Memory X-ray — launched from the Lessons tab. Two surfaces, by the dashboard's
// rule (scope-wide run → main column; drill into one row → right detail column):
//   • MAIN: the candidate cluster LIST (this view). A clustering agent groups
//     same-lesson-different-slug memories; the list is scannable, one scroll.
//   • RIGHT DETAIL: one cluster's DISTILLATION (ClusterDistillation, opened via
//     ctx.onOpenDetail) — the merge run + canonical memory + suggested recipients.
//
// Both passes cost tokens, so neither auto-fires: the view opens ARMED (a
// LaunchCard with a model picker) and clustering runs only on the button; a
// cluster distills only when you open it (the click is the consent). While an
// agent runs we stream its live reasoning so the wait is observable. Read-only:
// nothing is written back to any endpoint.

// Session caches so re-entering / re-rendering is instant; cleared on reload.
// Clusters depend on the model; the coverage matrix does not; merges depend on
// model + member set. Each caches on its OWN success (a transient failure
// retries on re-entry rather than poisoning the cache).
const clusterCache = new Map();
const matrixCache = new Map();
const mergeCache = new Map();
onAuthIdentityChange(() => {
  clusterCache.clear();
  matrixCache.clear();
  mergeCache.clear();
});

// Cap each cache so it can't grow unbounded over a long session. Eviction is
// FIFO (Map preserves insertion order) and safe: a re-entry after eviction just
// re-arms the launch gate / re-runs a distillation on open — no silent re-spend.
const CACHE_MAX = 32;
function cachePut(map, key, value) {
  map.set(key, value);
  if (map.size > CACHE_MAX) map.delete(map.keys().next().value);
}

const PREVIEW_TAIL = 240;

const GEN_ORDER = { "team-canon": 0, "project-specific": 1, unclear: 2, personal: 3 };
const GEN_LABEL = { "team-canon": "Team canon", "project-specific": "Project knowledge", personal: "Personal", unclear: "Unclear" };
const GEN_GROUPS = ["team-canon", "project-specific", "unclear", "personal"];

// Fold a sub-agent event stream into a small live-progress slot. Mirrors
// kernelSaveAgent's forwardChildEvent: turn_start → thinking, message_update
// text/thinking deltas → a rolling preview tail, tool calls → step count.
function applyAgentEvent(run, event) {
  if (!run || !event) return run;
  switch (event.type) {
    case "turn_start":
      return { ...run, activity: "thinking…" };
    case "tool_execution_start":
      return { ...run, steps: run.steps + 1, activity: `${event.toolName || "tool"}…` };
    case "tool_execution_end":
      return { ...run, activity: event.isError ? "retrying…" : "thinking…" };
    case "message_update": {
      const inner = event.assistantMessageEvent;
      if (!inner) return run;
      if (inner.type === "text_delta" && inner.delta) return { ...run, preview: (run.preview + inner.delta).slice(-PREVIEW_TAIL), activity: "writing…" };
      if (inner.type === "thinking_delta" && inner.delta) return { ...run, preview: (run.preview + inner.delta).slice(-PREVIEW_TAIL), activity: "reasoning…" };
      if (inner.type === "toolcall_start") return { ...run, activity: "preparing…" };
      return run;
    }
    default:
      return run;
  }
}

// A run-progress slot + an onEvent sink. `run` is null when idle.
function useAgentRun() {
  const [run, setRun] = useState(null);
  const start = useCallback(() => setRun({ activity: "starting…", preview: "", steps: 0, startedAt: Date.now() }), []);
  const stop = useCallback(() => setRun(null), []);
  const onEvent = useCallback((event) => setRun((r) => applyAgentEvent(r, event)), []);
  return { run, start, stop, onEvent };
}

// Live agent-run preview — the chat's tenant-profile busy-panel idiom: a
// contained panel with a one-line live read of the model's own streamed
// reasoning + a short caption. Same tokens, no bespoke chrome.
function AgentPreview({ title, caption, run }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const elapsed = run?.startedAt ? Math.max(0, Math.round((now - run.startedAt) / 1000)) : 0;
  const liveAction = (run?.preview && run.preview.trim()) || run?.activity || "starting…";
  return (
    <div className="flex min-w-0 flex-col gap-[8px]">
      {title ? <div className="text-[12px] leading-[1.3] text-[color:var(--ink)]">{title}</div> : null}
      <div className="flex min-w-0 flex-col gap-[10px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] p-[12px]">
        <div className="flex min-w-0 items-center gap-[7px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[10px] py-[7px] text-[11px] leading-[1.4]">
          <span className="inline-block h-[6px] w-[6px] shrink-0 animate-pulse rounded-full bg-[color:var(--green)]" />
          <span className="min-w-0 flex-1 truncate font-mono text-[color:var(--muted)]">{liveAction}</span>
          <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] tabular-nums text-[color:var(--muted-soft)]">{elapsed}s</span>
        </div>
        {caption ? <p className="m-0 text-[11px] leading-[1.5] text-[color:var(--muted-soft)]">{caption}</p> : null}
      </div>
    </div>
  );
}

export default function MemoryXrayView({ scopePath = [], range, onBack, onOpenDetail, onNavigatePath }) {
  const authContextEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
  const scopeKey = useMemo(() => JSON.stringify(scopePath), [scopePath]);
  const matrixKey = `${authContextEpoch}::${scopeKey}::${JSON.stringify(range) || "default"}`;
  const apiScope = useMemo(() => dashboardScopeToApiScope(scopePath).apiScope, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Model picker. Defaults to the SAME model the chat runs (creds.model — the
  // user's chosen primary model), so the X-ray and chat agree out of the box.
  // Picker drops to a cheaper model when wanted; the launch card gates the spend.
  const [creds, setCreds] = useState(null);
  useEffect(() => {
    let on = true;
    getChatCreds().then((c) => { if (on) setCreds(c); }).catch(() => { /* unauth/offline */ });
    return () => { on = false; };
  }, []);
  const [modelChoice, setModelChoice] = useState("");
  const defaultModelId = creds ? (creds.model || defaultModelFor(creds.provider) || "") : "";
  const xrayModel = modelChoice || defaultModelId;
  const modelOptions = useMemo(() => {
    if (!creds?.provider) return [];
    const ranked = getRankedModels(creds.provider).map(({ model }) => ({
      value: model.id,
      label: model.contextWindow ? `${model.name} · ${Math.round(model.contextWindow / 1000)}k` : model.name,
    }));
    return ranked.some((o) => o.value === xrayModel) || !xrayModel ? ranked : [{ value: xrayModel, label: xrayModel }, ...ranked];
  }, [creds?.provider, xrayModel]);

  const cacheKey = `${matrixKey}::${xrayModel || "auto"}`;

  // Explicit-spend gate: opens ARMED; clustering runs on the button (or instantly
  // on a cache hit). runTick forces a re-run.
  const [armedState, setArmedState] = useState(() => ({ authContextEpoch, value: true }));
  const armed = armedState.authContextEpoch === authContextEpoch ? armedState.value : true;
  const setArmed = (value) => setArmedState({ authContextEpoch, value });
  const [runTick, setRunTick] = useState(0);
  const emptyClusterState = { loading: false, error: null, clusters: [], notes: "", catalogSize: 0, authContextEpoch };
  const [state, setState] = useState(emptyClusterState);
  const visibleState = state.authContextEpoch === authContextEpoch ? state : emptyClusterState;
  const [matrixState, setMatrixState] = useState(() => ({ key: matrixKey, value: matrixCache.get(matrixKey) || null }));
  const matrix = matrixState.key === matrixKey ? matrixState.value : null;
  const cluster = useAgentRun();

  // Pass 1 — cluster the catalog by name. Gated on `armed`.
  useEffect(() => {
    if (clusterCache.has(cacheKey)) {
      if (armed) setArmed(false);
      setState({ ...clusterCache.get(cacheKey), loading: false, error: null, authContextEpoch });
      cluster.stop();
      return undefined;
    }
    if (armed || !creds?.provider) {
      setState(emptyClusterState);
      return undefined;
    }
    const ctrl = new AbortController();
    setState({ loading: true, error: null, clusters: [], notes: "", catalogSize: 0, authContextEpoch });
    cluster.start();
    clusterMemories({ apiScope, range, model: xrayModel || null, signal: ctrl.signal, onEvent: cluster.onEvent })
      .then((r) => {
        const next = { clusters: r.clusters, notes: r.notes, catalogSize: r.catalogSize };
        cachePut(clusterCache, cacheKey, next);
        setState({ ...next, loading: false, error: null, authContextEpoch });
      })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setState({ loading: false, error: e?.message || String(e), clusters: [], notes: "", catalogSize: 0, authContextEpoch });
      })
      .finally(() => cluster.stop());
    return () => ctrl.abort();
  }, [cacheKey, creds?.provider, armed, runTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Coverage matrix — cheap analytics query (no tokens). Runs once launched,
  // caches on its OWN success so a transient failure retries on re-entry.
  useEffect(() => {
    if (armed) return undefined;
    if (matrixCache.has(matrixKey)) { setMatrixState({ key: matrixKey, value: matrixCache.get(matrixKey) }); return undefined; }
    let cancelled = false;
    memoryEndpointMatrix({ apiScope, range })
      .then((mx) => { if (cancelled) return; cachePut(matrixCache, matrixKey, mx); setMatrixState({ key: matrixKey, value: mx }); })
      .catch((e) => { if (!cancelled) console.warn("[memory-xray] coverage matrix failed:", e); });
    return () => { cancelled = true; };
    // matrixKey encodes apiScope+range (same keying as the cluster effect's cacheKey).
  }, [matrixKey, armed]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const sorted = [...(visibleState.clusters || [])].sort(
      (a, b) => (GEN_ORDER[a.generalizability] ?? 9) - (GEN_ORDER[b.generalizability] ?? 9) || (b.member_slugs?.length || 0) - (a.member_slugs?.length || 0),
    );
    return GEN_GROUPS.map((g) => ({ gen: g, items: sorted.filter((c) => c.generalizability === g) })).filter((s) => s.items.length);
  }, [visibleState.clusters]);

  const launch = () => setArmed(false);
  const rerun = () => { clusterCache.delete(cacheKey); setArmed(false); setRunTick((t) => t + 1); };

  const openDistill = useCallback((c) => {
    const coverage = matrix ? clusterCoverage(c, matrix) : null;
    onOpenDetail?.({
      eyebrow: "Memory · Distill",
      title: c.theme,
      detailKey: `xray-distill:${c.theme}`,
      content: <ClusterDistillation cluster={c} coverage={coverage} apiScope={apiScope} range={range} model={xrayModel} onNavigatePath={onNavigatePath} />,
    });
  }, [matrix, apiScope, range, xrayModel, onOpenDetail, onNavigatePath]);

  const modelSelect = modelOptions.length ? (
    <Select
      value={xrayModel}
      onChange={(v) => setModelChoice(String(v))}
      options={modelOptions}
      ariaLabel="Model for the memory X-ray"
      align="right"
      triggerStyle={{ height: 30, minHeight: 0, fontSize: 11, backgroundColor: "var(--bg)", backgroundImage: "linear-gradient(var(--control-bg),var(--control-bg))" }}
      className="px-[9px]"
    />
  ) : null;

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid grid-cols-[minmax(0,1fr)] gap-[12px] pt-[12px] pb-[24px]">
      <div className="flex items-center justify-between gap-[10px]">
        <button
          type="button"
          onClick={onBack}
          className="flex min-h-[26px] items-center gap-[5px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[11px] leading-[1.2] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
        >
          ← Lessons
        </button>
        {!armed && !visibleState.loading ? (
          <div className="flex shrink-0 items-center gap-[8px]">
            <button
              type="button"
              onClick={rerun}
              className="shrink-0 whitespace-nowrap text-[11px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
            >
              Re-run
            </button>
            {modelSelect}
          </div>
        ) : null}
      </div>
      <PageHero
        eyebrow="Memory · X-ray"
        title="Shared lessons across the fleet"
        detail="Equivalent memories grouped into canonical lessons, including endpoints that do not hold them. Select a lesson to create a canonical memory."
      />

      {armed ? (
        <LaunchCard
          eyebrow="In-browser agent · spends tokens"
          description="Groups equivalent memory files in this scope into shared lessons. Processing starts when you launch it and uses model tokens. Creating a canonical memory is a separate step for each group."
          controls={
            modelSelect ? (
              <div className="flex items-center gap-[8px] flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Model</span>
                {modelSelect}
              </div>
            ) : <span className={NOTE_CLASS}>Resolving model…</span>
          }
          ctaLabel="Distill shared lessons"
          onLaunch={launch}
          disabled={!creds?.provider}
          disabledHint={!creds?.provider ? "Connect a model to run the X-ray." : null}
        />
      ) : visibleState.loading ? (
        <AgentPreview
          caption="Grouping equivalent memories across the fleet. This takes about a minute."
          run={cluster.run}
        />
      ) : visibleState.error ? (
        <div className="rounded-[2px] border border-red-500/40 bg-[color:var(--control-bg)] px-[12px] py-[10px] text-[12px] text-red-400">
          Couldn&apos;t analyze the memory store: {visibleState.error}
          <button type="button" onClick={rerun} className="ml-[8px] underline hover:no-underline">retry</button>
        </div>
      ) : !grouped.length ? (
        <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[12px] py-[12px] text-[12px] text-[color:var(--muted)]">
          No shared lessons found. The memories in this scope appear distinct.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] text-[11px] leading-[1.2] text-[color:var(--muted)]">
            <span><strong className="text-[color:var(--ink)]">{visibleState.clusters.length}</strong> shared lessons</span>
            <span>from <strong className="text-[color:var(--ink)]">{formatCompact(visibleState.catalogSize)}</strong> memories</span>
            {visibleState.notes ? <span className={NOTE_CLASS}>· {visibleState.notes}</span> : null}
          </div>
          {grouped.map((section) => (
            <div key={section.gen} className="grid gap-[6px]">
              <div className="flex items-center gap-[8px] pt-[2px]">
                <span className="text-[10px] uppercase tracking-[0.1em] text-[color:var(--eyebrow)]">{GEN_LABEL[section.gen]}</span>
                <span className={NOTE_CLASS}>{section.items.length}</span>
                <span className="h-px flex-1 bg-[color:var(--line-soft)]" />
              </div>
              {section.items.map((c) => (
                <ClusterRow
                  key={c.theme + c.member_slugs.join(",")}
                  cluster={c}
                  coverage={matrix ? clusterCoverage(c, matrix) : null}
                  onOpen={() => openDistill(c)}
                />
              ))}
            </div>
          ))}
        </>
      )}
    </ScrollArea>
  );
}

// One candidate cluster — a compact, scannable row. Clicking it opens the
// distillation in the right detail column. No inline expansion (that was the
// nested-scroll mess); the member chips + synthesis live in the detail.
function ClusterRow({ cluster, coverage, onOpen }) {
  const have = coverage ? coverage.haveEndpoints.length : null;
  const total = coverage ? coverage.total : null;
  const missing = coverage ? coverage.missingEndpoints.length : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group grid w-full gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[12px] py-[10px] text-left transition-colors hover:border-[color:var(--line-strong)]"
    >
      <div className="flex items-center justify-between gap-[10px]">
        <span className="truncate text-[13px] leading-[1.15] font-medium text-[color:var(--ink)]">{cluster.theme}</span>
        <span className="shrink-0 text-[11px] leading-[1.2] text-[color:var(--muted)] transition-colors group-hover:text-[color:var(--ink)]">Distill →</span>
      </div>
      {cluster.rationale ? <p className="truncate text-[11px] leading-[1.4] text-[color:var(--muted)]">{cluster.rationale}</p> : null}
      <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[2px] text-[11px] leading-[1.2] text-[color:var(--muted-soft)]">
        <span>{cluster.member_slugs.length} memories</span>
        {coverage ? (
          <span>
            {have} / {total} have{missing ? ` · ${missing} missing` : " · fleet-wide"}
          </span>
        ) : null}
      </div>
    </button>
  );
}

// Right-detail surface: distill ONE cluster. Auto-runs the merge on open (the
// click that opened this panel is the consent). Shows the cluster's members,
// the live run, then the canonical memory + suggested recipients. Brings its
// own scroll (the detail column mounts rich content directly).
function ClusterDistillation({ cluster, coverage, apiScope, range, model, onNavigatePath }) {
  const authContextEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
  const mergeKey = `${authContextEpoch}::${model || "auto"}::${cluster.member_slugs.join("|")}`;
  const [merge, setMerge] = useState(() =>
    mergeCache.has(mergeKey)
      ? { key: mergeKey, status: "done", result: mergeCache.get(mergeKey), error: null }
      : { key: mergeKey, status: "idle", result: null, error: null },
  );
  const visibleMerge = merge.key === mergeKey
    ? merge
    : { key: mergeKey, status: "idle", result: null, error: null };
  const ctrlRef = useRef(null);
  const run = useAgentRun();

  // Resolve endpoint labels for both sides of the coverage — where the cluster's
  // memories were SOURCED FROM (have) and where they're MISSING (propagate to).
  const have = coverage?.haveEndpoints || [];
  const missing = coverage?.missingEndpoints || [];
  const epSteps = useMemo(() => [...have, ...missing].map((id) => ({ dim: "endpoints", id })), [have.join(","), missing.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const labelTick = useLabels(epSteps);
  const sourceEndpoints = useMemo(() => have.map((id) => ({ id, label: scopeStepLabel({ dim: "endpoints", id }) })), [have.join(","), labelTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const recipientEndpoints = useMemo(() => missing.map((id) => ({ id, label: scopeStepLabel({ dim: "endpoints", id }) })), [missing.join(","), labelTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sample the originating traces: a source endpoint + the cluster's memories →
  // the traces on that endpoint that wrote or used any of these memories.
  const onSample = useCallback((endpointId) => {
    onNavigatePath?.([
      { dim: "endpoints", ids: [endpointId], id: endpointId },
      { dim: "memories", ids: cluster.member_slugs, id: cluster.member_slugs.join("|") },
    ]);
  }, [onNavigatePath, cluster.member_slugs]); // eslint-disable-line react-hooks/exhaustive-deps

  const runMerge = useCallback(() => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setMerge({ key: mergeKey, status: "loading", result: null, error: null });
    run.start();
    mergeCluster({ memberSlugs: cluster.member_slugs, apiScope, range, model: model || null, signal: ctrl.signal, onEvent: run.onEvent })
      .then((r) => { cachePut(mergeCache, mergeKey, r); setMerge({ key: mergeKey, status: "done", result: r, error: null }); })
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setMerge({ key: mergeKey, status: "error", result: null, error: e?.message || String(e) });
      })
      .finally(() => run.stop());
  }, [cluster.member_slugs, apiScope, range, model, mergeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-run on open unless already cached. Abort on close/unmount.
  useEffect(() => {
    if (!mergeCache.has(mergeKey)) runMerge();
    return () => ctrlRef.current?.abort();
  }, [mergeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[12px] px-[2px] py-[10px]">
      {visibleMerge.status === "loading" ? (
        <>
          {cluster.rationale ? <p className="m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]">{cluster.rationale}</p> : null}
          <MemberChips slugs={cluster.member_slugs} />
          <AgentPreview caption="Merging the cluster's memories into one canonical lesson, preserving every specific." run={run.run} />
        </>
      ) : visibleMerge.status === "error" ? (
        <div className="flex items-center gap-[8px] text-[11px] text-red-400">
          Distillation failed: {visibleMerge.error}
          <button type="button" onClick={runMerge} className="underline hover:no-underline">retry</button>
        </div>
      ) : visibleMerge.status === "done" && visibleMerge.result ? (
        <MergedMemory
          merged={visibleMerge.result.merged}
          sourceCount={visibleMerge.result.sources.length}
          memberSlugs={cluster.member_slugs}
          rationale={cluster.rationale}
          sourceEndpoints={sourceEndpoints}
          recipientEndpoints={recipientEndpoints}
          total={coverage?.total ?? null}
          onSample={onSample}
        />
      ) : null}
    </ScrollArea>
  );
}

function MemberChips({ slugs }) {
  return (
    <div className="flex flex-wrap gap-[4px]">
      {slugs.map((s) => (
        <span key={s} className="max-w-full truncate rounded-[3px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[5px] py-[1px] text-[10px] leading-[1.3] text-[color:var(--muted)]">
          {labelForMemory(s)}
        </span>
      ))}
    </div>
  );
}

function MergedMemory({ merged, sourceCount, memberSlugs, rationale, sourceEndpoints = [], recipientEndpoints = [], total, onSample }) {
  const [view, setView] = useState("rendered");
  const [showNuances, setShowNuances] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const nuances = merged.preserved_nuances || [];
  const conflicts = merged.conflicts || [];

  return (
    <div className="grid gap-[12px]">
      {/* The distilled artifact */}
      <div className="grid gap-[2px]">
        <div className="flex items-center gap-[6px]">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Canonical memory</span>
          <span className={NOTE_CLASS}>merged from {sourceCount}</span>
        </div>
        <div className="text-[14px] leading-[1.18] font-medium text-[color:var(--ink)]">{merged.name}</div>
        {merged.description ? <div className="text-[12px] leading-[1.4] text-[color:var(--muted)]">{merged.description}</div> : null}
      </div>

      {/* Coverage — where the cluster came FROM (samplable) and where it's
          MISSING (the give-back targets). Elevated above the body. */}
      {(sourceEndpoints.length || recipientEndpoints.length) ? (
        <div className="grid gap-[9px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[12px] py-[10px]">
          {sourceEndpoints.length ? (
            <div className="grid gap-[5px]">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[color:var(--muted-soft)]">
                Sourced from · {sourceEndpoints.length} endpoint{sourceEndpoints.length > 1 ? "s" : ""}
              </span>
              <div className="flex flex-wrap gap-[4px]">
                {sourceEndpoints.map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onSample?.(id)}
                    title="Sample the traces that wrote or used these memories on this endpoint"
                    className="group flex max-w-[200px] items-center gap-[4px] rounded-[3px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.3] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                  >
                    <span className="truncate">{label}</span>
                    <span aria-hidden className="text-[color:var(--muted-soft)] transition-colors group-hover:text-[color:var(--green)]">↗</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {recipientEndpoints.length ? (
            <div className={["grid gap-[5px]", sourceEndpoints.length ? "border-t border-[color:var(--line-soft)] pt-[8px]" : ""].join(" ")}>
              <span className="text-[10px] uppercase tracking-[0.1em] text-[color:var(--eyebrow)]">
                Propagate to · {recipientEndpoints.length}{total ? ` of ${total}` : ""} without it
              </span>
              <div className="flex flex-wrap gap-[4px]">
                {recipientEndpoints.map(({ id, label }) => (
                  <OverflowTooltip
                    key={id}
                    as="span"
                    text={label}
                    className="max-w-[200px] truncate rounded-[3px] border border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.3] text-[color:var(--muted)]"
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* The memory content — rendered | raw */}
      <div className="grid gap-[6px]">
        <div className="flex items-center justify-between gap-[8px]">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">Memory</span>
          <div className="inline-flex overflow-hidden rounded-[2px] border border-[color:var(--line)] text-[10px] leading-[1.1]">
            {["rendered", "raw"].map((m, i) => (
              <button
                key={m}
                type="button"
                onClick={() => setView(m)}
                className={[
                  "px-[7px] py-[2px] capitalize transition-colors",
                  i > 0 ? "border-l border-[color:var(--line)]" : "",
                  view === m ? "bg-[color:var(--control-bg)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                ].join(" ")}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[10px] py-[8px] text-[12px] leading-[1.5] text-[color:var(--ink)]">
          {view === "raw" ? (
            <pre className="m-0 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-[1.5] text-[color:var(--muted)]">{merged.body || ""}</pre>
          ) : (
            <Markdown text={merged.body || ""} />
          )}
        </div>
      </div>

      {/* Quality */}
      {(nuances.length || conflicts.length) ? (
        <div className="grid gap-[6px]">
          <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] text-[11px] leading-[1.2] text-[color:var(--muted)]">
            {nuances.length ? (
              <button type="button" onClick={() => setShowNuances((v) => !v)} className="underline hover:no-underline">
                {nuances.length} specifics preserved
              </button>
            ) : null}
            {conflicts.length ? <span className="text-amber-400">{conflicts.length} conflict{conflicts.length > 1 ? "s" : ""} to resolve</span> : null}
          </div>
          {showNuances && nuances.length ? (
            <ul className="grid gap-[3px] pl-[14px] text-[11px] leading-[1.4] text-[color:var(--muted)] [list-style:disc]">
              {nuances.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          ) : null}
          {conflicts.length ? (
            <div className="grid gap-[3px] rounded-[2px] border border-amber-500/40 px-[8px] py-[6px] text-[11px] leading-[1.4] text-amber-300">
              {conflicts.map((c, i) => <div key={i}>⚠ {c}</div>)}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Sources — provenance, de-emphasized */}
      <div className="grid gap-[6px] border-t border-[color:var(--line-soft)] pt-[8px]">
        <button
          type="button"
          onClick={() => setShowSources((v) => !v)}
          className="flex items-center gap-[6px] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)] transition-colors hover:text-[color:var(--muted)]"
        >
          <span aria-hidden>{showSources ? "▾" : "▸"}</span> Sources · {memberSlugs.length} memories
        </button>
        {showSources ? (
          <div className="grid gap-[6px]">
            {rationale ? <p className="m-0 text-[11px] leading-[1.45] text-[color:var(--muted)]">{rationale}</p> : null}
            <MemberChips slugs={memberSlugs} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
